import "./index.scss";

import { Box, Stack } from "@mui/material";
import { ReactComponent as IconMoveLeft } from "assets/images/icons/move-left.svg";
import { ReactComponent as IconMoveRight } from "assets/images/icons/move-right.svg";
import { ReactComponent as IconCopy } from "assets/images/square-buttons/duplicate.svg";
import { ReactComponent as IconTrash } from "assets/images/square-buttons/trash.svg";
import { StrengthProgram } from "models/workoutplan";
import { memo, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

const WorkoutPlanStrengthSetTargets = (props: {
  programs: StrengthProgram[];
  selectedProgram: string;
}) => {
  const { programs, selectedProgram } = props;
  const [program, setProgram] = useState<StrengthProgram>();
  const [maximumNumberOfSets, setMaximumNumberOfSets] = useState(0);
  const [selectedSetToManage, setSelectedSetToManage] = useState<{
    exerciseIndex: number;
    setIndex: number;
  }>();
  const formContext = useFormContext();
  const {
    setValue,
    formState: { errors },
  } = formContext;

  useEffect(() => {
    if (programs && selectedProgram) {
      let p = programs.find((f) => f.nanoId === selectedProgram);
      setProgram({ ...p });
    }
  }, [programs, selectedProgram]);

  useEffect(() => {
    if (program) {
      let maxNbSets = 0;
      program.exercises.forEach((e) => {
        if (e.sets.length > maxNbSets) maxNbSets = e.sets.length;
      });
      setMaximumNumberOfSets(maxNbSets);

      //#region Update form field
      let programsCopy = [...programs];
      let programIndex = programsCopy.findIndex((f) => f.nanoId === selectedProgram);
      programsCopy[programIndex] = { ...program };
      setValue("strength_workouts", programsCopy);
      //#endregion Update form field
    }
  }, [program, programs, selectedProgram, setValue]);

  // useEffect(() => {
  //   if (selectedSetToManage) {
  //     scrollToTop(".set-row.selected");
  //   }
  // }, [selectedSetToManage]);

  //#region event handlers
  const handleProgramFrequencyUpdate = (v: number) => {
    let programCopy = { ...program };
    programCopy.per_week = v;
    setProgram(programCopy);
  };

  const handleSetFieldValueUpdate = (exerciseIndex, setIndex, keyToUpdate, value) => {
    value = value.replace(/\D/g, "");
    let programCopy = { ...program };
    programCopy.exercises[exerciseIndex].sets[setIndex][keyToUpdate] = +value;
    setProgram(programCopy);
  };

  const handleMoveLeft = () => {
    if (!selectedSetToManage) {
      toast(`Click on a set entry below to manage it.`, { type: "info" });
      return;
    }
    let programCopy = { ...program };
    let setsCopy = programCopy.exercises[selectedSetToManage.exerciseIndex].sets;
    if (selectedSetToManage.setIndex > 0) {
      const from = selectedSetToManage.setIndex;
      const to = selectedSetToManage.setIndex - 1;
      setsCopy.splice(to, 0, setsCopy.splice(from, 1)[0]);
      setProgram(programCopy);
      setSelectedSetToManage({ ...selectedSetToManage, setIndex: to });
    }
  };

  const handleMoveRight = () => {
    if (!selectedSetToManage) {
      toast(`Click on a set entry below to manage it.`, { type: "info" });
      return;
    }
    let programCopy = { ...program };
    let setsCopy = programCopy.exercises[selectedSetToManage.exerciseIndex].sets;
    if (selectedSetToManage.setIndex < setsCopy.length - 1) {
      const from = selectedSetToManage.setIndex;
      const to = selectedSetToManage.setIndex + 1;
      setsCopy.splice(to, 0, setsCopy.splice(from, 1)[0]);
      setProgram(programCopy);
      setSelectedSetToManage({ ...selectedSetToManage, setIndex: to });
    }
  };

  const handleCopy = () => {
    if (!selectedSetToManage) {
      toast(`Click on a set entry below to manage it.`, { type: "info" });
      return;
    }
    let programCopy = { ...program };
    let setsCopy = programCopy.exercises[selectedSetToManage.exerciseIndex].sets;
    setsCopy.splice(selectedSetToManage.setIndex + 1, 0, {
      reps_max: setsCopy[selectedSetToManage.setIndex].reps_max,
      reps_min: setsCopy[selectedSetToManage.setIndex].reps_min,
      rest_time: setsCopy[selectedSetToManage.setIndex].rest_time,
      weight: setsCopy[selectedSetToManage.setIndex].weight,
    });
    setProgram(programCopy);
  };

  const handleDelete = () => {
    if (!selectedSetToManage) {
      toast(`Click on a set entry below to manage it.`, { type: "info" });
      return;
    }
    let programCopy = { ...program };
    let setsCopy = programCopy.exercises[selectedSetToManage.exerciseIndex].sets;
    setsCopy.splice(selectedSetToManage.setIndex, 1);
    setProgram(programCopy);
  };
  //#endregion event handlers

  return (
    <Stack className="workoutPlanStrengthSetTarget">
      <h2 className="headerSpacing">{program?.name}</h2>
      <Stack direction={"row"} alignContent="center" gap="40px" pl={"10px"} pb={2}>
        <Box className="fieldLabel" pt={"14px"}>
          Workouts per week
        </Box>
        <div className="base-input">
          <input
            type="number"
            min={1}
            value={program?.per_week}
            onChange={(e) => handleProgramFrequencyUpdate(+e.target.value)}
          />
        </div>
      </Stack>
      <h2 className="headerSpacing">Sets</h2>
      <Stack className="workout-plan-strength-exercise-targets-container" direction={"row"}>
        <Box>
          <Stack height={"100%"} marginTop="84px" gap={1}>
            {program?.exercises.map((e, i) => (
              <Box className="entryTitle" key={`exerciseTitle${i}`}>
                {e.name}
              </Box>
            ))}
          </Stack>
        </Box>
        <Stack>
          <Stack
            direction={"row"}
            gap={1.25}
            justifyContent={"flex-end"}
            pr={1.25}
            className="setsMgmtControls"
          >
            <IconMoveLeft onClick={handleMoveLeft} />
            <IconMoveRight onClick={handleMoveRight} />
            <IconCopy onClick={handleCopy} />
            <IconTrash onClick={handleDelete} />
          </Stack>
          <Box maxWidth={"100vw"} overflow="auto">
            <Stack className="setsMgmtArea" gap={1}>
              <Stack>
                {Array(maximumNumberOfSets)
                  .fill(0)
                  .map((_m, i) => (
                    <Box key={`setCounter${i}`} className="setNumber">
                      Set {i + 1}
                    </Box>
                  ))}
              </Stack>
              <Stack>
                {Array(maximumNumberOfSets)
                  .fill(0)
                  .map((_m, i) => (
                    <Stack direction={"row"} key={`setHeader${i}`} className="setHeaders">
                      <p>Weight</p>
                      <p>Min Reps</p>
                      <p>Max Reps</p>
                      <p>Rest Time</p>
                    </Stack>
                  ))}
              </Stack>
              {program?.exercises.map((m, exerciseIndex) => (
                <Stack key={`exerciseSetRow${exerciseIndex}`}>
                  {m.sets.map((set, setIndex) => (
                    <Stack
                      key={`exerciseSetCol${exerciseIndex}_${setIndex}`}
                      className={`set-row ${
                        selectedSetToManage?.exerciseIndex === exerciseIndex &&
                        selectedSetToManage?.setIndex === setIndex
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => setSelectedSetToManage({ exerciseIndex, setIndex })}
                    >
                      <input
                        value={set.weight === 9999 ? 0 : set.weight}
                        type="text"
                        min="0"
                        step="any"
                        onChange={(e) => {
                          handleSetFieldValueUpdate(
                            exerciseIndex,
                            setIndex,
                            "weight",
                            e.target.value
                          );
                        }}
                      />
                      <input
                        value={set.reps_min}
                        type="text"
                        min="0"
                        max="100"
                        onChange={(e) => {
                          handleSetFieldValueUpdate(
                            exerciseIndex,
                            setIndex,
                            "reps_min",
                            e.target.value
                          );
                        }}
                      />
                      <input
                        value={set.reps_max}
                        type="text"
                        min="0"
                        max="100"
                        onChange={(e) => {
                          handleSetFieldValueUpdate(
                            exerciseIndex,
                            setIndex,
                            "reps_max",
                            e.target.value
                          );
                        }}
                      />
                      <input
                        value={set.rest_time}
                        type="text"
                        min="0"
                        max="300"
                        onChange={(e) => {
                          handleSetFieldValueUpdate(
                            exerciseIndex,
                            setIndex,
                            "rest_time",
                            e.target.value
                          );
                        }}
                      />
                    </Stack>
                  ))}
                  {m.sets.length < maximumNumberOfSets &&
                    Array(maximumNumberOfSets - m.sets.length)
                      .fill(0)
                      .map((m, index) => (
                        <Stack
                          key={`exerciseSetCol${exerciseIndex}_${index}`}
                          className="set-row empty"
                        ></Stack>
                      ))}
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};
export default memo(WorkoutPlanStrengthSetTargets);
